import classnames from 'classnames';
import { diffLines } from 'diff';
import _ from 'lodash';
import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { PrismAsyncLight as Prism } from 'react-syntax-highlighter';

import { LoadingSkeleton } from 'src/app/graph/check/CheckSchemas/LoadingSkeleton';
import {
  assertUnreachable,
  assertUnreachableOrReturnDefault,
} from 'src/lib/assertUnreachable';

import { prismStudioThemeLight } from '../graphqlCodeBlock/prismStudioThemeLight';

import {
  DiffViewType,
  LineNumberPropsFunction,
  RangeExpandType,
  TextDiff,
  getRangesToShow,
  invertRangesInDiffView,
} from './diffViewHelpers';
import { GraphQLDiffViewLineRenderer } from './graphQLDiffViewLineRenderer/GraphQLDiffViewLineRenderer';

type LineVisibilityType = 'only-changes' | 'custom';

export const GraphQLDiffView = ({
  oldSdl,
  newSdl,
  className,
  lineVisibilityType,
  lineNumberClassName,
  lineProps,
  customLinesToShow,
  additionalVisibleLines,
  gutterMarkers,
  onMount,
  onDiffStats,
}: {
  oldSdl: string | undefined;
  newSdl: string | undefined;
  className?: string;
  lineVisibilityType?: LineVisibilityType;
  lineNumberClassName?: string;
  lineProps?: LineNumberPropsFunction | ReturnType<LineNumberPropsFunction>;
  gutterMarkers?: {
    lineNumber: number;
    type: DiffViewType;
    marker: React.ReactNode;
  }[];
  // This is used to force certain lines to be visible for when the hiddenRangesType is 'custom'
  customLinesToShow?: {
    lineNumber: number;
    type: DiffViewType;
  }[];
  additionalVisibleLines?: {
    lineNumber: number;
    type: DiffViewType;
  }[];
  onMount?: () => void;
  onDiffStats?: (stats: { added: number; removed: number }) => void;
}) => {
  const leftScrollRef = useRef<HTMLPreElement>(null);
  const rightScrollRef = useRef<HTMLPreElement>(null);

  const [diffsLoading, setDiffsLoading] = useState(true);
  const [textDiff, setTextDiff] = useState<TextDiff>(() => {
    const originalDiffLines = oldSdl?.split('\n') ?? [];
    const modifiedDiffLines = newSdl?.split('\n') ?? [];
    return {
      added: [],
      removed: [],
      originalDiffLines,
      originalDiffLineNumbers: originalDiffLines.map((__, i) => i + 1),
      modifiedDiffLines,
      modifiedDiffLineNumbers: modifiedDiffLines.map((__, i) => i + 1),
    };
  });

  useEffect(() => {
    let currOriginalLineNumber = 1;
    let currModifiedLineNumber = 1;
    setDiffsLoading(true);
    diffLines(oldSdl ?? '', newSdl ?? '', {
      timeout: 10 * 1000,
      callback: (diffs) => {
        setDiffsLoading(false);

        const awaitedTextDiff = diffs?.reduce(
          (acc: TextDiff, line, i) => {
            // Account for mismatched line numbers before adding unchanged lines
            if (!line.added && !line.removed) {
              while (
                acc.originalDiffLines.length < acc.modifiedDiffLines.length
              ) {
                acc.originalDiffLines.push(' ');
                acc.originalDiffLineNumbers.push(undefined);
              }
              while (
                acc.originalDiffLines.length > acc.modifiedDiffLines.length
              ) {
                acc.modifiedDiffLines.push(' ');
                acc.modifiedDiffLineNumbers.push(undefined);
              }
            }
            line.value
              .replace(/\n$/, '')
              .split('\n')
              .forEach((singleLine) => {
                if (!line.removed) {
                  acc.modifiedDiffLines.push(singleLine);
                  acc.modifiedDiffLineNumbers.push(currModifiedLineNumber++);
                  if (line.added) acc.added.push(acc.modifiedDiffLines.length);
                }
                if (!line.added) {
                  acc.originalDiffLines.push(singleLine);
                  acc.originalDiffLineNumbers.push(currOriginalLineNumber++);
                  if (line.removed)
                    acc.removed.push(acc.originalDiffLines.length);
                }
              });
            // Add any remaining padding to fill out the rest of the space after the last
            // diff item
            if (i === diffs.length - 1) {
              while (
                acc.originalDiffLines.length < acc.modifiedDiffLines.length
              ) {
                acc.originalDiffLines.push(' ');
                acc.originalDiffLineNumbers.push(undefined);
              }
              while (
                acc.originalDiffLines.length > acc.modifiedDiffLines.length
              ) {
                acc.modifiedDiffLines.push(' ');
                acc.modifiedDiffLineNumbers.push(undefined);
              }
            }

            return acc;
          },
          {
            added: [],
            removed: [],
            originalDiffLines: [],
            originalDiffLineNumbers: [],
            modifiedDiffLines: [],
            modifiedDiffLineNumbers: [],
          },
        );

        if (awaitedTextDiff) {
          setTextDiff(awaitedTextDiff);
          onDiffStats?.({
            added: awaitedTextDiff.added.length,
            removed: awaitedTextDiff.removed.length,
          });
        }
      },
    });
  }, [newSdl, oldSdl, onDiffStats]);

  const initialHiddenRanges = useMemo(() => {
    if (lineVisibilityType && textDiff) {
      const totalLineCount = Math.max(
        textDiff.originalDiffLines.length,
        textDiff.modifiedDiffLines.length,
      );

      const additionalLinesToShow =
        additionalVisibleLines?.flatMap(({ type, lineNumber }) => {
          // Original lines are on the left side
          // Modified lines are on the right side
          const lineNumberMap =
            type === 'original'
              ? textDiff.originalDiffLineNumbers
              : textDiff.modifiedDiffLineNumbers;
          const matchingLineNumber = lineNumberMap.findIndex(
            (convertedLineNumber) => convertedLineNumber === lineNumber,
          );

          // Since the lineNumberMap is indexed by 0, we need to add 1
          // to the matchingLineNumber to match the added and removed arrays which
          // are indexed by 1.
          return matchingLineNumber === -1 ? [] : matchingLineNumber + 1;
        }) ?? [];

      const customVisibleLinesToShow =
        customLinesToShow?.flatMap(({ type, lineNumber }) => {
          // Original lines are on the left side
          // Modified lines are on the right side
          const lineNumberMap =
            type === 'original'
              ? textDiff.originalDiffLineNumbers
              : textDiff.modifiedDiffLineNumbers;
          const matchingLineNumber = lineNumberMap.findIndex(
            (convertedLineNumber) => convertedLineNumber === lineNumber,
          );

          // Since the lineNumberMap is indexed by 0, we need to add 1
          // to the matchingLineNumber to match the added and removed arrays which
          // are indexed by 1.
          return matchingLineNumber === -1 ? [] : matchingLineNumber + 1;
        }) ?? [];

      const rangesToShow = getRangesToShow({
        linesToShow:
          lineVisibilityType === 'only-changes'
            ? [...textDiff.added, ...textDiff.removed, ...additionalLinesToShow]
            : lineVisibilityType === 'custom'
              ? [...customVisibleLinesToShow, ...additionalLinesToShow]
              : assertUnreachableOrReturnDefault(lineVisibilityType, []),
        totalLineCount,
      });

      return invertRangesInDiffView({
        ranges: rangesToShow,
        totalLineCount,
      });
    } else {
      return [];
    }
  }, [additionalVisibleLines, customLinesToShow, lineVisibilityType, textDiff]);

  // Because the diffs are loaded asynchronously, we need to wait until the diffs are loaded
  // before we can set the hidden ranges.
  const [hiddenRanges, setHiddenRanges] = useState(initialHiddenRanges);
  useEffect(() => {
    if (!diffsLoading) {
      setHiddenRanges(initialHiddenRanges);
    }
  }, [diffsLoading, initialHiddenRanges]);

  const handleExpandRange = useCallback(
    (expandType: RangeExpandType, lineNumber: number) => {
      const originalRange = hiddenRanges.find(
        (range) =>
          range.startLineNumber <= lineNumber &&
          lineNumber <= range.endLineNumber,
      );

      // This includes all ranges except the one being clicked
      const filteredHiddenRanges = hiddenRanges.filter(
        (range) => range.endLineNumber !== lineNumber,
      );
      if (!originalRange) return;
      switch (expandType) {
        case 'top':
          setHiddenRanges(
            filteredHiddenRanges.concat({
              ...originalRange,
              startLineNumber: originalRange.startLineNumber + 10,
            }),
          );
          break;
        case 'bottom':
          setHiddenRanges(
            filteredHiddenRanges.concat({
              ...originalRange,
              endLineNumber: originalRange.endLineNumber - 10,
            }),
          );
          break;
        case 'full':
          setHiddenRanges(filteredHiddenRanges);
          break;
        default:
          assertUnreachable(expandType);
      }
    },
    [hiddenRanges, setHiddenRanges],
  );

  if (diffsLoading) {
    return (
      <div
        className={classnames(
          'flex flex-wrap divide-x divide-primary overflow-auto text-xs',
          className,
        )}
      >
        <LoadingSkeleton className="flex-1 py-2" />
        <LoadingSkeleton className="flex-1 py-2" />
      </div>
    );
  }

  return (
    <div
      className={classnames('flex flex-wrap overflow-auto text-xs', className)}
    >
      <Prism
        language="graphql"
        style={prismStudioThemeLight}
        PreTag={({
          style,
          ...props
        }: {
          className?: string;
          style: CSSProperties;
        }) => (
          <pre
            {...props}
            ref={leftScrollRef}
            className={props.className}
            onScroll={(scroll) => {
              rightScrollRef.current?.scroll({
                left: scroll.currentTarget.scrollLeft,
              });
            }}
            style={{
              ..._.omit(
                style,
                'fontSize',
                'fontFamily',
                'padding',
                'margin',
                'background',
              ),
              flex: '1 1 0%',
            }}
          />
        )}
        renderer={({ rows, stylesheet, useInlineStyles }) =>
          rows.map((node, i) => (
            <GraphQLDiffViewLineRenderer
              node={node}
              stylesheet={stylesheet}
              useInlineStyles={useInlineStyles}
              index={i}
              textDiff={textDiff}
              lineNumberClassName={lineNumberClassName}
              renderHiddenRanges={!!lineVisibilityType}
              hiddenRanges={hiddenRanges}
              handleExpandRange={handleExpandRange}
              gutterMarkers={gutterMarkers}
              lineProps={lineProps}
              onMount={onMount}
              type="original"
              // eslint-disable-next-line react/no-array-index-key
              key={`original-code-segment-${i}`}
            />
          ))
        }
      >
        {textDiff.originalDiffLines.join('\n')}
      </Prism>
      <Prism
        language="graphql"
        style={prismStudioThemeLight}
        PreTag={({
          style,
          ...props
        }: {
          className?: string;
          style: CSSProperties;
        }) => (
          <pre
            {...props}
            ref={rightScrollRef}
            className={classnames(props.className, 'border-l border-primary')}
            onScroll={(scroll) => {
              leftScrollRef.current?.scroll({
                left: scroll.currentTarget.scrollLeft,
              });
            }}
            style={{
              ..._.omit(
                style,
                'fontSize',
                'fontFamily',
                'padding',
                'margin',
                'background',
              ),
              flex: '1 1 0%',
            }}
          />
        )}
        renderer={({ rows, stylesheet, useInlineStyles }) =>
          rows.map((node, i) => (
            <GraphQLDiffViewLineRenderer
              node={node}
              stylesheet={stylesheet}
              useInlineStyles={useInlineStyles}
              index={i}
              textDiff={textDiff}
              lineNumberClassName={lineNumberClassName}
              renderHiddenRanges={!!lineVisibilityType}
              hiddenRanges={hiddenRanges}
              handleExpandRange={handleExpandRange}
              gutterMarkers={gutterMarkers}
              lineProps={lineProps}
              onMount={onMount}
              type="modified"
              // eslint-disable-next-line react/no-array-index-key
              key={`modified-code-segment-${i}`}
            />
          ))
        }
      >
        {textDiff.modifiedDiffLines.join('\n')}
      </Prism>
    </div>
  );
};
